import { getId, uniqueBy } from "@indietabletop/appkit";
import type { SpaceGits } from "@indietabletop/types";
import * as fmt from "../ui/fmt.ts";
import type { ModelIssue, WeaponsMaxExceeded } from "./common.ts";
import { getAddedRefs, getUpdatedWeaponRefs } from "./diffs.ts";
import { EditMode } from "./EditMode.ts";
import { getEffectsFor, modifyDiceType } from "./effects.ts";
import type { Gang, VehiclePermissions } from "./Gang.ts";
import type { Repair } from "./Repair.ts";
import { rollTimes } from "./rollTimes.ts";
import type { VehicleWeaponPlacement } from "./Ruleset.ts";
import { VehicleWeapon } from "./Weapon.ts";

export class Vehicle {
  gang: Gang;
  data: SpaceGits.VehicleData;

  constructor(gang: Gang, data: SpaceGits.VehicleData) {
    this.gang = gang;
    this.data = data;
  }

  get id() {
    return this.data.id;
  }

  get name() {
    return this.data.name || this.type.name;
  }

  get isNamed() {
    return !!this.data.name;
  }

  get isWriteOff() {
    return this.effects.some((effect) => effect.type === "REMOVE_MODEL");
  }

  get causeOfWriteOff() {
    return this.repairs
      .find((t) =>
        t.valueOrNull()?.effects.some((e) => e.type === "REMOVE_MODEL"),
      )
      ?.valueOrNull();
  }

  get isBuzzcopter() {
    return this.type.id === "buzzcopter";
  }

  get originalType() {
    return this.ruleset.vehicles.get(this.data.type.id).valueOrThrow();
  }

  get type() {
    // We must only reference *purchased* mods here, as otherwise we can run
    // into circural reference issues (effects affect type, but type dictates\
    // mods, which contain effects...)
    const effectsOfNonDefaultMods = [
      ...this.purchasedMods.flatMap((l) => l.valueOrNull()?.effects ?? []),
      ...this.repairs.flatMap((l) => l.valueOrNull()?.effects ?? []),
    ];

    const effects = getEffectsFor(effectsOfNonDefaultMods, {
      type: "SET_STAT",
      stat: "type",
      data: this.data,
    });

    const typeId = effects.reduce(
      (_, effect) => effect.value,
      this.data.type.id,
    );

    return this.ruleset.vehicles.get(typeId).valueOrThrow();
  }

  get ruleset() {
    return this.gang.ruleset;
  }

  get baseCost() {
    // Intentionally using original cost, as type can only be changed via
    // repairs that the user doesn't pay for and therefore adjusting the
    // cost because a repair was selected feels weird.
    return this.originalType.cost;
  }

  get cost() {
    return (
      this.baseCost +
      this.weapons
        .filter((r) => r.isSuccess)
        .map((r) => r.value)
        .reduce((acc, w) => acc + w.cost * w.count, 0) +
      this.purchasedMods
        .filter((r) => r.isSuccess)
        .map((g) => g.value)
        .reduce((acc, g) => acc + g.cost, 0)
    );
  }

  get displayCost() {
    switch (this.gang.mode.capUsageStyle) {
      case "BASE_COST": {
        return this.baseCost;
      }

      case "FULL_COST": {
        return this.cost;
      }

      default: {
        return null;
      }
    }
  }

  get engine() {
    const engineEffects = getEffectsFor(this.effects, {
      type: "INCREMENT_STAT",
      stat: "engine",
      data: this.data,
    });

    return modifyDiceType(this.type.engine, engineEffects);
  }

  get seats() {
    const seatEffects = getEffectsFor(this.effects, {
      type: "INCREMENT_STAT",
      stat: "seats",
      data: this.data,
    });

    const value = seatEffects.reduce(
      (acc, effect) => acc + effect.value,
      this.type.seats,
    );

    return Math.max(1, value);
  }

  get toughness() {
    const toughnessEffects = getEffectsFor(this.effects, {
      type: "INCREMENT_STAT",
      stat: "toughness",
      data: this.data,
    });

    const value = toughnessEffects.reduce(
      (acc, effect) => acc + effect.value,
      this.type.toughness,
    );

    return Math.max(1, value);
  }

  get rules() {
    return this.type.rules.map((ref) =>
      this.ruleset.vehicleSpecialRules.get(ref.id),
    );
  }

  get weaponRefs() {
    return this.data.weapons;
  }

  get weapons() {
    return this.weaponRefs
      .map((ref, index) => {
        return this.ruleset.weapons
          .get(ref.id)
          .mapSuccess((w) => new VehicleWeapon(this, index, ref, w.data));
      })
      .sort((left, right) => {
        if (left.isSuccess && right.isSuccess) {
          return left.value.vehicleWeaponCompare(right.value);
        }

        // Do not sort failures.
        return 0;
      });
  }

  get weaponSpecialRules() {
    return uniqueBy(
      this.weapons.filter((w) => w.isSuccess).flatMap((w) => w.value.rules),
      (item) => (item.isSuccess ? item.value.id : item.failure.missingKey),
    );
  }

  get weaponsCount() {
    return this.weapons.reduce(
      (acc, weapon) => acc + (weapon.valueOrNull()?.count ?? 0),
      0,
    );
  }

  get defaultMods() {
    return this.type.mods.map((ref) => {
      return this.ruleset.mods.get(ref.id);
    });
  }

  get modRefs() {
    return this.data.mods;
  }

  get purchasedMods() {
    return this.modRefs.map((ref) => {
      return this.ruleset.mods.get(ref.id);
    });
  }

  get mods() {
    return [...this.defaultMods, ...this.purchasedMods];
  }

  get repairRefs() {
    return this.data.repairs;
  }

  get repairs() {
    return this.repairRefs.map((ref) => {
      return this.ruleset.repairLocations
        .get(ref.locationId)
        .flatMap((location) => location.repairs.get(ref.id));
    });
  }

  get campaign() {
    return this.data.campaign ?? [];
  }

  get loadoutSummary() {
    return fmt.list([
      ...this.weapons.map((lookup) => {
        return lookup.mapSuccess((w) => {
          return w.count > 1
            ? `${w.name} x${w.count} (${w.placement})`
            : `${w.name} (${w.placement})`;
        });
      }),
      ...this.mods,
      ...this.repairs,
    ]);
  }

  get weaponCostLimit() {
    return this.gang.mode.isRandomBuildMode ? 0 : Infinity;
  }

  can(permission: EditMode, subject: VehiclePermissions) {
    return !!(this.gang.mode.permissions[subject] & permission);
  }

  get hasPremiumContent() {
    return !!this.mods.length || !!this.repairs.length;
  }

  get issues() {
    if (this.gang.mode.isCheatModeActive) {
      return [];
    }

    const issues = new Map<string, ModelIssue>();

    if (this.weaponsCount > this.seats) {
      const issue: WeaponsMaxExceeded = {
        type: "WEAPONS_MAX_EXCEEDED",
        max: this.seats,
        current: this.weaponsCount,
      };

      issues.set(issue.type, issue);
    }

    return Array.from(issues.values());
  }

  get sortValue() {
    const sortTable: Record<string, number> = {
      buzzcopter: 1,
      scrap_tank: 2,
      truck: 3,
      car: 4,
      half_track: 5,
      forklift: 6,
      buggy: 7,
      bike_with_sidecar: 8,
      bike: 9,
    };

    return sortTable[this.type.id] ?? 10;
  }

  private get effects() {
    return [
      ...this.mods.flatMap((lookup) => lookup.valueOrNull()?.effects ?? []),
      ...this.repairs.flatMap((lookup) => lookup.valueOrNull()?.effects ?? []),
    ];
  }

  compare(otherVehicle: Vehicle) {
    if (this.sortValue === otherVehicle.sortValue) {
      return this.name.localeCompare(otherVehicle.name);
    }

    return this.sortValue - otherVehicle.sortValue;
  }

  setRandomLoot(options: { count: number }) {
    this.data.weapons = [];
    this.data.mods = [];

    const looted = rollTimes(
      options.count,
      () => this.ruleset.getRandomVehicleLoot(),
      {
        getKey: (loot) => loot.item.id,
        shouldReroll: (loot, rolled) => {
          // If we rolled a weapon but we already have the maximum number of
          // weapons present on this vehicle, roll again.
          if (loot.type === "WEAPON") {
            const weaponsRolled = Array.from(rolled.values()).filter(
              (item) => item.type === "WEAPON",
            );

            return weaponsRolled.length >= this.seats;
          }

          return false;
        },
      },
    );

    for (const loot of looted) {
      if (loot.type === "WEAPON") {
        const placement: VehicleWeaponPlacement =
          this.data.weapons.length === 0 ? "FIXED" : "PINTLE_MOUNTED";

        this.data.weapons.push({ id: loot.item.id, placement, count: 1 });
      }

      if (loot.type === "MOD") {
        this.data.mods.push({ id: loot.item.id });
      }
    }

    return this;
  }

  setRandomRepairs(options: { count: number }) {
    this.data.repairs = [];

    const repairs = rollTimes(
      options.count,
      () => {
        return this.ruleset.getRandomRepairLocation().getRandomRepair();
      },
      {
        getKey: (repair) => `${repair.location.id}/${repair.id}`,
        shouldReroll: (repair) => {
          return (
            // Skip repairs that would result in the model being removed
            repair.effects.some((e) => e.type === "REMOVE_MODEL") ||
            // If the repair is based on a mod, we want to reroll it if that
            // mod is already present on this vehicle.
            (repair.fromMod &&
              this.data.mods.some((ref) => ref.id === repair.id))
          );
        },
      },
    );

    for (const repair of repairs) {
      this.data.repairs.push({ id: repair.id, locationId: repair.location.id });
    }

    return this;
  }

  getWeaponsDiff(
    nextWeaponRefs: SpaceGits.VehicleWeaponRef[],
  ): SpaceGits.VehicleWeaponsChangedDiff | null {
    const updated = getUpdatedWeaponRefs(this.weaponRefs, nextWeaponRefs);

    if (updated.length === 0) {
      return null;
    }

    return {
      type: "VEHICLE_WEAPONS_CHANGED",
      updated: updated,
      createdTs: Date.now(),
    };
  }

  addWeapons(weapons: SpaceGits.VehicleWeaponRef[]) {
    const { isCampaignMode, isCheatModeActive } = this.gang.mode;

    if (isCampaignMode && !isCheatModeActive) {
      const diff = this.getWeaponsDiff(weapons);
      return this.clone({
        campaign: diff ? [...this.campaign, diff] : this.campaign,
        weapons,
      });
    }

    return this.clone({ weapons });
  }

  removeWeapon(atIndex: number) {
    return this.clone({
      weapons: this.data.weapons.filter((_, i) => i !== atIndex),
    });
  }

  getModsDiff(nextRefs: SpaceGits.Ref[]): SpaceGits.ModsChangedDiff | null {
    const added = getAddedRefs(this.modRefs, nextRefs);

    if (added.length === 0) {
      return null;
    }

    return {
      type: "MODS_CHANGED",
      createdTs: Date.now(),
      added,
    };
  }

  addMods(mods: SpaceGits.Ref[]) {
    const { isCampaignMode, isCheatModeActive } = this.gang.mode;

    if (isCampaignMode && !isCheatModeActive) {
      const diff = this.getModsDiff(mods);
      return this.clone({
        campaign: diff ? [...this.campaign, diff] : this.campaign,
        mods,
      });
    }

    return this.clone({ mods });
  }

  removeMod(atIndex: number) {
    return this.clone({
      mods: this.data.mods.filter((_, i) => i !== atIndex),
    });
  }

  getRepairsDiff(
    nextRefs: SpaceGits.RepairRef[],
    isPaid: boolean,
  ): SpaceGits.RepairsChangedDiff | null {
    const added = getAddedRefs(this.repairRefs, nextRefs);

    if (added.length === 0) {
      return null;
    }

    return {
      type: "REPAIRS_CHANGED",
      createdTs: Date.now(),
      isPaid,
      added,
    };
  }

  addRepairs(repairs: SpaceGits.TreatmentRef[], isPaid: boolean) {
    const { isCampaignMode, isCheatModeActive } = this.gang.mode;

    if (isCampaignMode && !isCheatModeActive) {
      const diff = this.getRepairsDiff(repairs, isPaid);
      return this.clone({
        campaign: diff ? [...this.campaign, diff] : this.campaign,
        repairs,
      });
    }

    return this.clone({ repairs });
  }

  removeRepair(atIndex: number) {
    return this.clone({
      repairs: this.data.repairs.filter((_, i) => i !== atIndex),
    });
  }

  strip() {
    this.data.weapons = [];
    this.data.mods = [];
    this.data.repairs = [];

    return this;
  }

  revertWriteOff(cause: Repair) {
    const repairs = this.repairRefs.filter((ref) => !cause.matchesRef(ref));

    const campaign = this.campaign.filter((item) => {
      return !(
        item.type === "REPAIRS_CHANGED" &&
        item.added.some((ref) => cause.matchesRef(ref))
      );
    });

    return this.clone({ repairs, campaign });
  }

  clone(overrides?: Partial<SpaceGits.VehicleData>) {
    return this.gang
      .clone({
        vehicles: this.gang.data.vehicles.map((data) => {
          if (data.id === this.id) {
            return { ...data, ...overrides };
          }

          return data;
        }),
      })
      .getVehicle(this.id)
      .valueOrThrow();
  }

  static initData(data: {
    name?: string;
    type: SpaceGits.VehicleData["type"];
  }): SpaceGits.VehicleData {
    return {
      ...data,
      id: getId(),
      name: data.name ?? "",
      type: data.type,
      mods: [],
      repairs: [],
      weapons: [],
    };
  }
}
